import { Controller } from "@hotwired/stimulus";

const FORM_CONFIG: Record<string, { title: string; subtitle: string; placeholder: string }> = {
  bug: {
    title: "Report a bug",
    subtitle: "We typically respond within a few hours",
    placeholder: "What happened? What did you expect to happen?",
  },
  general_help: {
    title: "General help",
    subtitle: "We typically respond within a few hours",
    placeholder: "What do you need help with?",
  },
  dse: {
    title: "Talk to your DSE",
    subtitle: "Your Developer Success Engineer is here to help",
    placeholder: "How can we help with your evaluation?",
  },
};

export default class extends Controller {
  static targets = [
    "surfacePanel",
    "formPanel",
    "formTitle",
    "formSubtitle",
    "supportTypeField",
    "subjectField",
    "messageField",
    "submitButton",
    "toast",
    "actualForm",
  ];

  declare readonly surfacePanelTarget: HTMLElement;
  declare readonly formPanelTarget: HTMLElement;
  declare readonly formTitleTarget: HTMLElement;
  declare readonly formSubtitleTarget: HTMLElement;
  declare readonly supportTypeFieldTarget: HTMLInputElement;
  declare readonly subjectFieldTarget: HTMLInputElement;
  declare readonly messageFieldTarget: HTMLTextAreaElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly toastTarget: HTMLElement;
  declare readonly actualFormTarget: HTMLFormElement;

  private isSubmitting = false;
  private pendingType: string | null = null;
  private pendingSubject: string | null = null;
  private boundHandleDrawerToggle = this.handleDrawerToggle.bind(this);
  private boundCaptureTrigger = this.captureTrigger.bind(this);

  connect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.addEventListener("toggle", this.boundHandleDrawerToggle);
    }

    // Capture trigger button data BEFORE the popover steals focus
    document.addEventListener("click", this.boundCaptureTrigger, true);
  }

  disconnect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.removeEventListener("toggle", this.boundHandleDrawerToggle);
    }
    document.removeEventListener("click", this.boundCaptureTrigger, true);
  }

  // Capture click on any trigger button before the popover opens
  private captureTrigger(event: Event) {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      '[popovertarget="support-drawer"]',
    );
    if (button) {
      this.pendingType = button.dataset.supportType || null;
      this.pendingSubject = button.dataset.subject || null;
    }
  }

  private handleDrawerToggle(event: Event) {
    const drawer = event.target as HTMLElement & { matches: (selector: string) => boolean };

    if (drawer.matches(":popover-open")) {
      this.showSurface();

      // If the trigger specified a type, skip surface and go straight to form
      if (this.pendingType) {
        const type = this.pendingType;
        const subject = this.pendingSubject;
        this.pendingType = null;
        this.pendingSubject = null;

        setTimeout(() => {
          this.transitionToForm(type);
          if (subject) {
            this.subjectFieldTarget.value = subject;
          }
        }, 0);
      }
    }
  }

  // Called by card buttons on the surface panel
  showForm(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const type = button.dataset.type || "general_help";
    this.transitionToForm(type);
  }

  // Called by back button and cancel button on the form panel
  showSurface() {
    this.formPanelTarget.classList.add("hidden");
    this.formPanelTarget.classList.remove("flex");
    this.surfacePanelTarget.classList.remove("hidden");
    this.resetForm();
  }

  private transitionToForm(type: string) {
    const config = FORM_CONFIG[type] || FORM_CONFIG.general_help;

    // Update form header
    this.formTitleTarget.textContent = config.title;
    this.formSubtitleTarget.textContent = config.subtitle;

    // Set hidden field and placeholder
    this.supportTypeFieldTarget.value = type;
    this.messageFieldTarget.placeholder = config.placeholder;

    // Toggle panels
    this.surfacePanelTarget.classList.add("hidden");
    this.formPanelTarget.classList.remove("hidden");
    this.formPanelTarget.classList.add("flex");
  }

  handleSubmit(event: Event) {
    event.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    const form = event.target as HTMLFormElement;
    const submitButton = this.submitButtonTarget;

    if (submitButton) {
      submitButton.disabled = true;
      const originalText = submitButton.textContent || "Send request";
      submitButton.textContent = "Submitting...";
      submitButton.dataset.originalText = originalText;
    }

    const formData = new FormData(form);

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response
            .json()
            .catch(() => ({ success: false, error: `Server error: ${response.status}` }));
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          this.showToast(data.message || "Thanks! We'll be in touch shortly.", "success");

          setTimeout(() => {
            const drawer = document.getElementById("support-drawer") as HTMLElement & {
              hidePopover?: () => void;
            };
            if (drawer && drawer.hidePopover) {
              drawer.hidePopover();
            }
            this.showSurface();
          }, 2000);
        } else {
          let errorMessage: string;
          if (data.errors && Array.isArray(data.errors)) {
            errorMessage = data.errors.join(", ");
          } else {
            errorMessage = data.error || "Oops, something went wrong. Please try again.";
          }
          this.showToast(errorMessage, "error");
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        this.showToast("Oops, something went wrong. Please try again.", "error");
      })
      .finally(() => {
        this.isSubmitting = false;
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = submitButton.dataset.originalText || "Send request";
        }
      });
  }

  private resetForm() {
    this.actualFormTarget.reset();
    this.supportTypeFieldTarget.value = "general_help";
    this.toastTarget.classList.add("hidden");
  }

  showToast(message: string, type: "success" | "error") {
    const toast = this.toastTarget;
    toast.textContent = message;
    toast.classList.remove(
      "bg-green-500",
      "bg-red-500",
      "text-black",
      "text-white",
      "border-l-4",
      "border-green-700",
      "border-red-700",
      "p-4",
      "rounded",
      "hidden",
    );

    toast.classList.add("border-l-4", "p-4", "rounded");

    if (type === "success") {
      toast.classList.add("bg-green-500", "text-black", "border-green-700");
    } else {
      toast.classList.add("bg-red-500", "text-white", "border-red-700");
    }

    setTimeout(() => {
      toast.classList.add("hidden");
    }, 3000);
  }
}
